// Unit Code related types and interfaces

import type { AssignmentForStudent } from './assignment';

/** Standalone unit (no parent course) from GET /api/Unit/without-course */
export interface StandaloneUnitDto {
  id: number;
  unitName: string;
  titel: string;
  unitTitle: string;
  price: number;
  imagePath?: string | null;
  teacherId?: string | null;
  active: boolean;
  order: number;
  isIndividual: boolean;
  creationDate?: string;
}

export interface PaginatedStandaloneUnits {
  currentPage: number;
  pageSize: number;
  totalPages: number;
  totalCount: number;
  hasPreviousPage: boolean;
  hasNextPage: boolean;
  data: StandaloneUnitDto[];
}

export interface StandaloneUnitsResponse {
  success: boolean;
  message: string;
  data: PaginatedStandaloneUnits;
}

export interface StandaloneUnitLessonDto {
  id: number;
  lessonName: string;
  titel: string;
  order: number;
  active: boolean;
  type: string;
}

export interface StandaloneUnitDetails {
  id: number;
  unitName: string;
  titel: string;
  price: number;
  imagePath?: string | null;
  teacherId?: string | null;
  active: boolean;
  order: number;
  creationDate?: string;
  lessons: StandaloneUnitLessonDto[];
}

export interface StandaloneUnitDetailsResponse {
  success: boolean;
  message: string;
  data: StandaloneUnitDetails;
}

export interface UnitCodeDto {
  Code: string;
  Grade: string;
  UnitName: string;
}

export interface MyUnitCodeDto {
  unitId: number;
  unitName: string;
  unitTitle: string;
  courseId: string;
  courseName: string;
  grade: string;
  term: string;
  active: boolean;
  enrollmentDate?: string;
}

export interface UnitLessonDto {
  id: number;
  lessonName: string;
  title: string;
  order: number;
  type: LessonType;
  active: boolean;
}

// New lesson type with progress information
export interface UnitLessonWithProgressDto {
  id: number;
  lessonName: string;
  titel: string;
  order: number;
  active: boolean;
  type: LessonType;
  isCompleted: boolean | null;
  isQuizSubmitted: boolean | null;
  isAssignmentSubmitted?: boolean | null;
  isFileOpened?: boolean | null;
}

// New unit data with progress and enrollment date
export interface UnitWithProgressDto {
  id: number;
  unitName: string;
  titel: string;
  active: boolean;
  order: number;
  enrollmentDate: string;
  lessons: UnitLessonWithProgressDto[];
}

export enum LessonType {
  Video = 'Video',
  Quiz = 'Quiz',
  Assignment = 'Assignment',
  File = 'File',
}

// API Response types
export interface UnitCodeResponse {
  success: boolean;
  message: string;
  data: UnitCodeDto;
}

export interface MyUnitCodesResponse {
  success: boolean;
  message: string;
  data: {
    totalCount: number;
    totalPages: number;
    currentPage: number;
    pageSize: number;
    units: MyUnitCodeDto[];
  };
}

export interface UnitLessonsResponse {
  success: boolean;
  message: string;
  data: {
    unitId: number;
    unitName: string;
    unitTitle: string;
    courseName: string;
    totalCount: number;
    totalPages: number;
    currentPage: number;
    pageSize: number;
    lessons: UnitLessonDto[];
  };
}

// New API response type for tree-unit-with-progress endpoint
export interface UnitWithProgressResponse {
  success: boolean;
  message: string;
  data: UnitWithProgressDto;
}

// Lesson Content types
export interface VideoLessonContent {
  id: number;
  videoUrl: string;
  attachmentUrl?: string;
  attachmentTitle?: string;
}

export interface QuizAnswer {
  text: string;
  isCorrect: boolean;
}

export interface QuizQuestion {
  text: string;
  type: 'MultipleChoice' | 'TrueFalse';
  answers: QuizAnswer[];
}

export interface QuizLessonContent {
  questions: QuizQuestion[];
  isRetake: boolean;
  isScoreDisplayed: boolean;
  isAnswerDisplayed: boolean;
  passDegree: number;
}

export type LessonContent = VideoLessonContent | QuizLessonContent | AssignmentForStudent;

export interface LessonContentResponse {
  success: boolean;
  data: LessonContent;
}

// UI State types
export interface UnitState {
  units: MyUnitCodeDto[];
  currentUnit: MyUnitCodeDto | null;
  lessons: UnitLessonDto[];
  currentLesson: UnitLessonDto | null;
  isLoading: boolean;
  error: string | null;
  totalCount: number;
  totalPages: number;
  currentPage: number;
}

// Form types
export interface EnterCodeForm {
  code: string;
} 